import { useMemo } from "react";
import { AuthUserType } from "../auth/types";

type PermissionFlags = {
  canViewPatients: boolean;
  canCreatePatient: boolean;
  canEditPatient: boolean;
  canDeletePatient: boolean;
  canExportPatients: boolean;
};

/**
 * Centralized patient permissions mapping based on `user.roleId`.
 * Mirrors backend RolesEnum and route guards for consistency.
 */
export const usePatientPermissions = (user?: AuthUserType) => {
  return useMemo<PermissionFlags>(() => {
    const role = user?.roleId;

    const isAdmin = role === "admin";
    const isReception = role === "assistant" || role === "receptionist";
    const isClinician = role === "professional" || role === "clinician";
    const isManager = role === "manager" || role === "accountant";
    const isAccountant = role === "accountant";
    const isPatient = role === "patient";

    if (isAdmin) {
      return {
        canViewPatients: true,
        canCreatePatient: true,
        canEditPatient: true,
        canDeletePatient: true,
        canExportPatients: true,
      };
    }

    // Role-based permissions
    const canViewPatients = Boolean(role) && !isPatient;
    const canCreatePatient = isReception || isClinician || isManager;
    const canEditPatient = isManager;
    const canDeletePatient = isManager || isReception;
    const canExportPatients = isManager || isAccountant;

    return {
      canViewPatients,
      canCreatePatient,
      canEditPatient,
      canDeletePatient,
      canExportPatients,
    };
  }, [user?.roleId]);
};

export type { PermissionFlags };
